import axios from 'axios';
import { postData } from '../../services/axiosUtils';

const asListResponse = (data: any) => {
  if (Array.isArray(data)) {
    return { hasError: false, records: data, errorMessages: [] };
  }
  if (data?.records) {
    return data;
  }
  return { hasError: false, records: data ? [data] : [], errorMessages: [] };
};

const wrapEntity = (data: any) => {
  if (data?.hasError !== undefined) {
    return data;
  }
  return { hasError: false, result: data, errorMessages: [] };
};

const tenantService = {
  list: () => {
    return axios.get(`/api/tenants/list`).then((response) => asListResponse(response.data));
  },
  listMyTenants: () => {
    return axios.get(`/api/tenants/ListMyTenants`).then((response) => {
      const rows = Array.isArray(response.data) ? response.data : [];
      return rows.map((x: any) => ({
        tenantId: x.id ?? x.Id,
        tenantName: x.name ?? x.Name,
        isDefault: x.isDefault ?? x.IsDefault,
        membershipId: x.membershipId ?? x.MembershipId,
      }));
    });
  },
  get: (id: any) => {
    return axios.get(`/api/tenants/details/${id}`).then((response) => wrapEntity(response.data));
  },
  create: (entity: any) => {
    return postData({
      url: `/api/tenants/create`,
      data: {
        name: entity.name,
        slug: entity.slug,
        type: entity.type ?? entity.tenantType ?? 1,
        contactEmail: entity.contactEmail,
        contactName: entity.contactName,
        region: entity.region,
        defaultLanguage: entity.defaultLanguage || 'hu',
        timeZone: entity.timeZone || 'Europe/Budapest',
        tenantGroupId: entity.tenantGroupId ?? entity.TenantGroupId ?? null,
        groupDisplayName: entity.groupDisplayName ?? entity.GroupDisplayName ?? null,
        deepLinkSlug: entity.deepLinkSlug ?? entity.DeepLinkSlug ?? null,
      },
    }).then((response) => wrapEntity(response));
  },
  changeTenant: (entity: any) => {
    return postData({
      url: `/api/tenants/changeTenant`,
      data: { tenantId: entity.tenantId ?? entity.TenantId },
    }).then((response) => {
      return response?.hasError !== undefined
        ? response
        : { hasError: false, result: { tenantId: response?.tenantId ?? response?.TenantId } };
    });
  },
  setDefaultTenant: (entity: any) => {
    return postData({
      url: `/api/tenants/setDefaultTenant`,
      data: { tenantId: entity.tenantId ?? entity.TenantId },
    }).then((response) => {
      return response?.hasError !== undefined
        ? response
        : {
            hasError: false,
            result: {
              tenantId: response?.tenantId ?? response?.TenantId,
              isDefault: true,
            },
          };
    });
  },
  update: (entity: any) => {
    return postData({
      url: `/api/tenants/update`,
      data: entity,
      method: 'PUT',
    }).then((response) => wrapEntity(response));
  },
  suspend: (id: any) => {
    return postData({
      url: `/api/tenants/suspend/${id}`,
    }).then((response) => wrapEntity(response));
  },
  delete: (id: any) => {
    return postData({
      url: `/api/tenants/delete/${id}`,
      method: 'DELETE',
    }).then((response) => wrapEntity(response));
  },
  getSettings: (id: any) =>
    axios.get(`/api/tenants/${id}/settings`).then((response) => wrapEntity(response.data)),
  updateSettings: (id: any, data: any) =>
    postData({ url: `/api/tenants/${id}/settings`, data, method: 'PUT' }).then(wrapEntity),
  getBranding: (id: any) =>
    axios.get(`/api/tenants/${id}/branding`).then((response) => wrapEntity(response.data)),
  updateBranding: (id: any, data: any) =>
    postData({ url: `/api/tenants/${id}/branding`, data, method: 'PUT' }).then(wrapEntity),
  uploadBrandingLogo: (id: any, data: { fileName: string; contentBase64: string }) =>
    postData({
      url: `/api/tenants/${id}/branding/logo`,
      data: {
        fileName: data.fileName,
        contentBase64: data.contentBase64,
      },
    })
      .then(wrapEntity)
      .catch((error) => {
        const data = error?.response?.data;
        if (data?.hasError !== undefined) {
          return data;
        }
        throw error;
      }),
  listApiCredentials: (id: any) =>
    axios.get(`/api/tenants/${id}/api-credentials`).then((response) => asListResponse(response.data)),
  rotateApiCredential: (id: any, data: any = {}) =>
    postData({ url: `/api/tenants/${id}/api-credentials/rotate`, data }).then(wrapEntity),
  unsuspend: (id: any) =>
    postData({ url: `/api/tenants/unsuspend/${id}` }).then((response) => wrapEntity(response)),
  getOverview: (id: any) =>
    axios.get(`/api/tenants/${id}/overview`).then((response) => wrapEntity(response.data)),
  listMembers: (id: any) =>
    axios.get(`/api/tenants/${id}/members`).then((response) => asListResponse(response.data)),
  setMemberRoles: (tenantId: any, membershipId: any, roleTemplateIds: string[]) =>
    postData({
      url: `/api/tenants/${tenantId}/members/${membershipId}/roles`,
      data: { roleTemplateIds },
      method: 'PUT',
    }).then(wrapEntity),
  impersonate: (id: any) =>
    postData({ url: `/api/tenants/impersonate/${id}` }).then((response) => wrapEntity(response)),
};

export default tenantService;
